// Census format detection — given parsed sheet headers, identify the source
// payroll/census format with a confidence rating and explanatory reasons.

const lowerSet = (headers) =>
  new Set(headers.map((h) => (h || '').toLowerCase().replace(/\*/g, '').trim()));

function has(set, name) {
  return set.has(name.toLowerCase());
}
function hasAny(set, names) {
  return names.some((n) => has(set, n));
}

// Each rule returns { match: bool, format, confidence, reasons[], matchedHeaders[] }
function checkStandard(headers) {
  const set = lowerSet(headers);
  const checks = [
    { key: 'annual salary', label: 'Annual Salary' },
    { key: 'filing status', label: 'Filing Status' },
    { key: 'worker type', label: 'Worker Type' },
  ];
  const matched = checks.filter((c) => has(set, c.key));
  if (matched.length >= 2) {
    return {
      match: true,
      format: 'standard',
      confidence: matched.length === 3 ? 'high' : 'medium',
      reasons: [`Matched ${matched.length}/3 signature columns from the HealthCues standard template`],
      matchedHeaders: matched.map((c) => c.label),
    };
  }
  return { match: false };
}

function checkHealthCues(headers) {
  const set = lowerSet(headers);
  // TriMed Census tab
  const sigA = has(set, 'gross wages per pay period');
  const sigB = hasAny(set, ['federal marital/filing status', 'federal marital filing status']);
  const sigC = has(set, 'pay schedule');
  const sigD = has(set, 'yearly');
  const score = [sigA, sigB, sigC, sigD].filter(Boolean).length;
  if (score >= 3) {
    return {
      match: true,
      format: 'healthcues',
      confidence: 'high',
      reasons: ['Matched the HealthCues partner template signature columns'],
      matchedHeaders: [
        sigA && 'Gross Wages Per Pay Period',
        sigB && 'Federal Marital/Filing Status',
        sigC && 'Pay Schedule',
        sigD && 'Yearly',
      ].filter(Boolean),
    };
  }
  return { match: false };
}

function checkAdp(headers) {
  const set = lowerSet(headers);
  // ADP Workforce Now exports include earnings codes + employer-tax columns.
  const earnings = ['reg', 'ot', 'hol'].filter((c) => has(set, c));
  const erTax = ['futaer', 'ssecer', 'medier'].filter((c) => has(set, c));
  if (earnings.length >= 2 && erTax.length >= 2) {
    return {
      match: true,
      format: 'adp_export',
      confidence: 'high',
      reasons: [
        `Found ADP earnings codes: ${earnings.join(', ').toUpperCase()}`,
        `Found ADP employer-tax columns: ${erTax.join(', ').toUpperCase()}`,
      ],
      matchedHeaders: [...earnings, ...erTax].map((c) => c.toUpperCase()),
    };
  }
  return { match: false };
}

function checkPaycom(headers) {
  const set = lowerSet(headers);
  const sigA = hasAny(set, ['regular', 'regular hours']);
  const sigB = hasAny(set, ['overtime', 'overtime hours']);
  const sigC = hasAny(set, ['employee number', 'employee #', 'employee id']);
  if (sigA && sigB && sigC) {
    return {
      match: true,
      format: 'paycom_export',
      confidence: 'medium',
      reasons: ['Matched Paycom signature columns: Regular + Overtime + Employee Number'],
      matchedHeaders: ['Regular', 'Overtime', 'Employee Number'],
    };
  }
  return { match: false };
}

function checkPaycor(headers) {
  const set = lowerSet(headers);
  const sigA = has(set, 'reg');
  const sigB = has(set, 'ot');
  const sigC = has(set, 'pto');
  // Avoid colliding with ADP — Paycor lacks the FUTAER/SSECER block
  const adpHit = ['futaer', 'ssecer'].some((c) => has(set, c));
  if (sigA && sigB && sigC && !adpHit) {
    return {
      match: true,
      format: 'paycor_export',
      confidence: 'medium',
      reasons: ['Matched Paycor signature columns: REG + OT + PTO without ADP employer-tax columns'],
      matchedHeaders: ['REG', 'OT', 'PTO'],
    };
  }
  return { match: false };
}

function checkGusto(headers) {
  const set = lowerSet(headers);
  const sigA = hasAny(set, ['regular_hours', 'regular hours']);
  const sigB = hasAny(set, ['overtime_hours', 'overtime hours']);
  if (sigA || sigB) {
    return {
      match: true,
      format: 'gusto_export',
      confidence: 'medium',
      reasons: ['Matched Gusto-style snake_case hours columns'],
      matchedHeaders: ['regular_hours', 'overtime_hours'].filter((h) => has(set, h)),
    };
  }
  return { match: false };
}

function checkGeneric(headers) {
  const set = lowerSet(headers);
  const hasName = [...set].some((h) => /name|first/.test(h));
  const hasSalary = [...set].some((h) => /salary|wages|gross|pay|income|compensation/.test(h));
  if (hasName && hasSalary) {
    return {
      match: true,
      format: 'generic',
      confidence: 'low',
      reasons: ['Found name + salary-like columns but no recognized payroll-system signature'],
      matchedHeaders: [...set].filter((h) => /name|first|salary|wages|gross|pay/.test(h)),
    };
  }
  return { match: false };
}

const RULES = [checkStandard, checkHealthCues, checkAdp, checkPaycom, checkPaycor, checkGusto, checkGeneric];

export function detectFormat(sheet) {
  if (!sheet || !sheet.headers || sheet.headers.length === 0) {
    return {
      format: 'unknown',
      confidence: 'none',
      reasons: ['No headers detected in the file.'],
      matchedHeaders: [],
    };
  }
  for (const rule of RULES) {
    const result = rule(sheet.headers);
    if (result.match) {
      return {
        format: result.format,
        confidence: result.confidence,
        reasons: result.reasons,
        matchedHeaders: result.matchedHeaders,
      };
    }
  }
  return {
    format: 'unknown',
    confidence: 'none',
    reasons: ['Could not match any known format'],
    matchedHeaders: [],
  };
}

export const FORMAT_LABELS = {
  standard: 'HealthCues Engine standard template',
  healthcues: 'HealthCues partner census template',
  adp_export: 'ADP Workforce Now export',
  paycom_export: 'Paycom export',
  paycor_export: 'Paycor export',
  gusto_export: 'Gusto export',
  generic: 'Generic spreadsheet (best-effort)',
  unknown: 'Unknown format',
};

export const ALL_FORMATS = Object.keys(FORMAT_LABELS);
